import { notificarSyncDesktop } from './adeDesktopSync';

const CHANNEL = 'ade-desktop-v1';

const DB_TEMPO_CORTE = 'ade-tempo-corte-db';
const DB_TEMPO_CORTE_VERSION = 9;
const STORE_RELATORIO = 'relatorioPecas';
const KEY_RELATORIO = 'relatorioPecas';
const LS_RELATORIO_LEGADO = 'relatorio-pecas-ade';

export type RelatorioCabecalhoPersist = {
  lote: string;
  nomeProduto: string;
  tempo: string;
  dataInicio: string;
  dataTermino: string;
};

export type RelatorioItemPersist = {
  id: string;
  corteNumero: number;
  nomePeca: string;
  quantidade: number;
  medidaMenor: number;
  medidaMaior: number;
  material: 'aglomerado' | 'mdf';
  espessura: number;
  lote: string;
  inicio: string;
  termino: string;
  observacao: string;
  tempoPorPecaMinutos?: number;
  tempoPorPecaManual?: boolean;
  planoOrigem: number;
  dataInclusao: string;
  ocultoRelatorio?: boolean;
  riscadoSobra?: boolean;
  pastaSobraId?: string | null;
};

type RelatorioBlobV2 = {
  v: 2;
  itens: RelatorioItemPersist[];
  cabecalho: RelatorioCabecalhoPersist;
  revision: number;
};

export type RelatorioEstadoPersist = {
  itens: RelatorioItemPersist[];
  cabecalho: RelatorioCabecalhoPersist;
  revision: number;
};

const CABECALHO_VAZIO: RelatorioCabecalhoPersist = {
  lote: '',
  nomeProduto: '',
  tempo: '',
  dataInicio: '',
  dataTermino: '',
};

function abrirDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_TEMPO_CORTE, DB_TEMPO_CORTE_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_RELATORIO)) {
        db.createObjectStore(STORE_RELATORIO);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function lerRawDb(): Promise<unknown> {
  return abrirDb().then(
    (db) =>
      new Promise<unknown>((resolve, reject) => {
        const tx = db.transaction(STORE_RELATORIO, 'readonly');
        const req = tx.objectStore(STORE_RELATORIO).get(KEY_RELATORIO);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
        tx.onerror = () => db.close();
      })
  );
}

function gravarRawDb(data: RelatorioBlobV2 | RelatorioItemPersist[]): Promise<void> {
  return abrirDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_RELATORIO, 'readwrite');
        tx.objectStore(STORE_RELATORIO).put(data, KEY_RELATORIO);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

function normalizarBlob(raw: unknown): RelatorioEstadoPersist | null {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    return {
      itens: raw as RelatorioItemPersist[],
      cabecalho: { ...CABECALHO_VAZIO },
      revision: 1,
    };
  }

  if (typeof raw === 'object' && (raw as RelatorioBlobV2).v === 2) {
    const b = raw as RelatorioBlobV2;
    return {
      itens: Array.isArray(b.itens) ? b.itens : [],
      cabecalho: b.cabecalho ?? { ...CABECALHO_VAZIO },
      revision: typeof b.revision === 'number' && b.revision > 0 ? b.revision : 1,
    };
  }

  return null;
}

function migrarLegadoLocalStorage(): RelatorioEstadoPersist | null {
  try {
    const saved = localStorage.getItem(LS_RELATORIO_LEGADO);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return null;
    return {
      itens: parsed as RelatorioItemPersist[],
      cabecalho: { ...CABECALHO_VAZIO },
      revision: 1,
    };
  } catch {
    return null;
  }
}

export async function lerRelatorioEstado(): Promise<RelatorioEstadoPersist> {
  try {
    const raw = await lerRawDb();
    const norm = normalizarBlob(raw);
    if (norm) return norm;
  } catch {
    /* ignora */
  }

  const legado = migrarLegadoLocalStorage();
  if (legado) {
    await salvarRelatorioEstado(legado);
    return legado;
  }

  return { itens: [], cabecalho: { ...CABECALHO_VAZIO }, revision: 0 };
}

export async function salvarRelatorioEstado(
  estado: RelatorioEstadoPersist
): Promise<RelatorioEstadoPersist> {
  const revision =
    typeof estado.revision === 'number' && estado.revision > 0 ? estado.revision + 1 : 1;
  const blob: RelatorioBlobV2 = {
    v: 2,
    itens: estado.itens,
    cabecalho: estado.cabecalho,
    revision,
  };
  await gravarRawDb(blob);
  try {
    localStorage.setItem(LS_RELATORIO_LEGADO, JSON.stringify(estado.itens));
  } catch {
    /* quota */
  }
  return { ...estado, revision };
}

export function notificarRelatorioAtualizado(revision: number): void {
  const msg = { tipo: 'relatorio-atualizado' as const, revision };
  notificarSyncDesktop('relatorio');
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(msg);
    bc.close();
  } catch {
    /* ignora */
  }
  try {
    const runtime = (window as unknown as { nw?: { Window?: { getAll?: () => Array<{ window?: Window }> } } }).nw;
    for (const janela of runtime?.Window?.getAll?.() ?? []) {
      try {
        janela.window?.postMessage(msg, '*');
      } catch {
        /* ignora */
      }
    }
  } catch {
    /* ignora */
  }
}

export function escutarRelatorioAtualizado(
  onAtualizado: (revision: number) => void
): () => void {
  const tratar = (data: unknown) => {
    const m = data as { tipo?: string; revision?: number };
    if (m?.tipo === 'relatorio-atualizado' && typeof m.revision === 'number') {
      onAtualizado(m.revision);
    }
  };

  const onWindowMessage = (e: MessageEvent) => tratar(e.data);
  window.addEventListener('message', onWindowMessage);

  let bc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => tratar(e.data);
  } catch {
    /* ignora */
  }

  return () => {
    window.removeEventListener('message', onWindowMessage);
    bc?.close();
  };
}

/** Lê → aplica mutação → grava → notifica outras janelas. */
export async function atualizarRelatorioEstado(
  mutar: (prev: RelatorioEstadoPersist) => RelatorioEstadoPersist
): Promise<RelatorioEstadoPersist> {
  const prev = await lerRelatorioEstado();
  const draft = mutar(prev);
  const saved = await salvarRelatorioEstado(draft);
  notificarRelatorioAtualizado(saved.revision);
  return saved;
}
